import { useTranslation } from 'react-i18next';
import { stripLinks } from './shared/LinkText';
import { WeekFilter } from './ui/week-filter';
import { matchesWeek } from '../lib/weekFilter';
import i18n from '../i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import {
  Plus, Trash2, UserCheck, Link2, Copy, RefreshCw, Mail, Phone, Globe,
  Paperclip, Download, X, Share2, Send, AlertTriangle, MailCheck, MailX, Eye,
  GitMerge,
} from 'lucide-react';
import EmployeeForm from './EmployeeForm';
import { EMPTY_EMPLOYEE_FORM, populateFormFromEmployee, prepareSubmitData } from './shared/employeeFormUtils';
import usePermissions from '../hooks/usePermissions';
import useValueLabels from '../hooks/useValueLabels';
import { IntakeTemplatesManager, useIntakeTemplates } from './IntakeTemplatesEditor';
import { dateLocale } from '../lib/dateFormat';
import { fmtDateWeek } from '../lib/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: i18n.t('auto.lbl.new'), color: 'bg-blue-100 text-blue-700' },
  screening: { label: i18n.t('auto.lbl.screening'), color: 'bg-amber-100 text-amber-700' },
  interviewed: { label: i18n.t('auto.lbl.interviewed'), color: 'bg-violet-100 text-violet-700' },
  offered: { label: i18n.t('auto.lbl.offered'), color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: i18n.t('auto.lbl.rejected'), color: 'bg-slate-200 text-slate-600' },
};
const STATUSES = Object.keys(STATUS_META);

const fmtDate = (d) => fmtDateWeek(d, '—');

/** Files the applicant attached to their own intake submission. */
const DEFAULT_DOC_TYPES = [
  'Contract', 'Passport', 'ID Card', 'Driving Licence', 'Visa', 'Work Permit',
  'Certificate', 'Photo', 'Drawing', 'Invoice', 'Report', 'Other',
];

const CandidateFiles = ({ candidateId, canEdit }) => {
  const { t } = useTranslation();
  const vl = useValueLabels();
  const [files, setFiles] = useState([]);
  const [docTypes, setDocTypes] = useState(DEFAULT_DOC_TYPES);

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then((r) => setDocTypes(r.data?.attachment_categories?.length
        ? r.data.attachment_categories : DEFAULT_DOC_TYPES))
      .catch(() => { /* keep the defaults */ });
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/candidates/${candidateId}/files`);
      setFiles(data || []);
    } catch { setFiles([]); }
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t('auto.misc.deleteThisFile'))) return;
    try {
      await axios.delete(`${API}/candidate-files/${id}`);
      toast.success(t('auto.misc.fileDeleted'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const setCategory = async (fid, category) => {
    try {
      await axios.patch(`${API}/candidate-files/${fid}`, { category: category || null });
      setFiles((prev) => prev.map((f) => (f.id === fid ? { ...f, category: category || null } : f)));
    } catch { toast.error(t('auto.misc.couldNotChangeTheDocumentType')); }
  };

  const open = async (f) => {
    try {
      const resp = await axios.get(`${API}/candidate-files/${f.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank');
    } catch { toast.error(t('auto.misc.couldNotOpenFile')); }
  };

  if (!files.length) {
    return <p className="text-sm text-slate-500" data-testid="candidate-files-empty">{t('auto.misc.noAttachmentsWereUploaded')}</p>;
  }

  return (
    <ul className="space-y-2" data-testid="candidate-files-list">
      {files.map((f) => (
        <li key={f.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <Paperclip className="h-4 w-4 text-slate-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{f.filename}</p>
              <p className="text-xs text-slate-500">
                {Math.round((f.size || 0) / 1024)} KB · {fmtDate(f.uploaded_at)}
              </p>
              {/* Document type the applicant chose — same list the app
                  uses for employee attachments, and it follows them
                  through a hire. */}
              {canEdit ? (
                <select
                  className="mt-1 text-xs border border-slate-300 rounded px-1.5 py-1 bg-white"
                  value={f.category || ''}
                  onChange={(e) => setCategory(f.id, e.target.value)}
                  data-testid={`candidate-file-category-${f.id}`}
                >
                  <option value="">{t('auto.misc.setDocumentType')}</option>
                  {docTypes.map((d) => <option key={d} value={d}>{vl(d)}</option>)}
                </select>
              ) : (
                <Badge className="bg-slate-100 text-slate-700 text-[10px] mt-1">
                  {vl(f.category) || f.kind}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="outline" size="sm" onClick={() => open(f)} data-testid={`candidate-file-open-${f.id}`}>
              <Download className="h-3.5 w-3.5" />
            </Button>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => remove(f.id)} data-testid={`candidate-file-delete-${f.id}`}>
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </Button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

/**
 * Fold a duplicate application into the record we already have: fill the
 * blanks (or take the newer answers), carry documents and notes across,
 * then drop the duplicate card.
 */
const MergeDialog = ({ candidate, onClose, onMerged }) => {
  const { t } = useTranslation();
  const [target, setTarget] = useState(null);
  const [strategy, setStrategy] = useState('fill_blanks');
  const [busy, setBusy] = useState(false);
  const matches = candidate?.duplicate_matches || [];

  useEffect(() => {
    setTarget(matches[0] ? `${matches[0].kind}:${matches[0].id}` : null);
    setStrategy('fill_blanks');
  }, [candidate?.id]); // eslint-disable-line

  const merge = async () => {
    if (!target) return;
    const [kind, id] = target.split(':');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/candidates/${candidate.id}/merge`, {
        target_kind: kind, target_id: id, strategy,
      });
      const bits = [];
      if (data.fields_filled?.length) bits.push(`${data.fields_filled.length} blank fields filled`);
      if (data.fields_overwritten?.length) bits.push(`${data.fields_overwritten.length} updated`);
      if (data.files_moved) bits.push(`${data.files_moved} document(s) moved`);
      toast.success(bits.length ? `Merged — ${bits.join(', ')}` : 'Merged');
      onMerged();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not merge');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!candidate} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="candidate-merge-dialog">
        <DialogHeader>
          <DialogTitle>Merge {candidate?.name} into an existing record</DialogTitle>
          <DialogDescription>
            The existing record is kept — this application is folded into it and the
            duplicate card disappears. Rates, department and positions are never touched.
          </DialogDescription>
        </DialogHeader>

        {matches.length === 0 ? (
          <p className="text-sm text-slate-600" data-testid="candidate-merge-empty">
            {t('auto.misc.noMatchingRecordFoundForThis')}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">{t('auto.misc.mergeInto')}</Label>
              {matches.map((m) => {
                const value = `${m.kind}:${m.id}`;
                return (
                  <label
                    key={value}
                    className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer ${
                      target === value ? 'border-slate-800 bg-slate-50' : 'border-slate-200'
                    }`}
                    data-testid={`candidate-merge-target-${m.id}`}
                  >
                    <input
                      type="radio"
                      className="mt-1"
                      checked={target === value}
                      onChange={() => setTarget(value)}
                    />
                    <span className="text-sm">
                      <strong>{m.name || 'Unnamed'}</strong>
                      <span className="text-slate-500">
                        {' '}— existing {m.kind === 'employee' ? 'employee' : 'candidate'}
                        , same {m.matched_on?.join(' and ')}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="space-y-2">
              <Label className="text-xs">{t('auto.misc.whatShouldWin')}</Label>
              {[
                ['fill_blanks', 'Only fill in what is missing', 'Existing answers are kept; empty fields get the new ones.'],
                ['prefer_new', 'Prefer the new application', 'Newer answers replace the old ones where they differ.'],
              ].map(([value, title, hint]) => (
                <label
                  key={value}
                  className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer ${
                    strategy === value ? 'border-slate-800 bg-slate-50' : 'border-slate-200'
                  }`}
                  data-testid={`candidate-merge-strategy-${value}`}
                >
                  <input
                    type="radio"
                    className="mt-1"
                    checked={strategy === value}
                    onChange={() => setStrategy(value)}
                  />
                  <span className="text-sm">
                    <strong>{title}</strong>
                    <span className="block text-xs text-slate-500">{hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="candidate-merge-cancel">{t('auto.misc.cancel')}</Button>
          <Button
            onClick={merge}
            disabled={!target || busy}
            data-testid="candidate-merge-confirm"
          >
            <GitMerge className="h-4 w-4 mr-1.5" /> {busy ? t('auto.misc.merging') : t('auto.misc.merge')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Amber banner listing people this candidate might already be. */
const DuplicateWarning = ({ matches, testId = 'duplicate-warning' }) => {
  if (!matches?.length) return null;
  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
      data-testid={testId}
    >
      <p className="font-medium flex items-center gap-1.5">
        <AlertTriangle className="h-4 w-4" />
        Possible duplicate — we already have {matches.length === 1 ? 'a record' : `${matches.length} records`} like this
      </p>
      <ul className="mt-1.5 space-y-0.5 text-xs">
        {matches.map((m) => (
          <li key={`${m.kind}-${m.id}`}>
            <strong>{m.name || 'Unnamed'}</strong> — existing {m.kind === 'employee' ? 'employee' : 'candidate'}
            , same {m.matched_on?.join(' and ')}
            {m.email ? ` (${m.email})` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
};

const INVITE_META = {
  sent: { label: i18n.t('auto.lbl.sent'), color: 'bg-blue-100 text-blue-700', Icon: MailCheck },
  opened: { label: i18n.t('auto.lbl.opened'), color: 'bg-violet-100 text-violet-700', Icon: Eye },
  submitted: { label: i18n.t('auto.lbl.applied'), color: 'bg-emerald-100 text-emerald-700', Icon: UserCheck },
  failed: { label: i18n.t('auto.lbl.notSent'), color: 'bg-red-100 text-red-700', Icon: MailX },
};

/** Email the form out and watch what happens to each invite. */
const InvitePanel = ({ link, canShare, onSent }) => {
  const { t } = useTranslation();
  const [invites, setInvites] = useState([]);
  const [emails, setEmails] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  // Which language the email (and the form it opens) should be in.
  const [inviteLang, setInviteLang] = useState('en');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/candidate-intake-links/${link.id}/invites`);
      setInvites(data || []);
    } catch { setInvites([]); }
  }, [link.id]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const list = emails.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
    if (!list.length) { toast.error(t('auto.misc.addAtLeastOneEmailAddress')); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/candidate-intake-links/${link.id}/invite`, {
        recipients: list,
        message: message.trim() || null,
        base_url: window.location.origin,
        language: inviteLang,
      });
      if (data.sent) toast.success(`Invite sent to ${data.sent} ${data.sent === 1 ? 'person' : 'people'}`);
      if (data.failed) {
        toast.error(
          data.invites.find((i) => i.error)?.error
          || `${data.failed} could not be sent — copy the link instead`,
        );
      }
      setEmails('');
      setMessage('');
      load();
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send');
    } finally { setSending(false); }
  };

  const resend = async (id) => {
    try {
      await axios.post(`${API}/candidate-intake-invites/${id}/resend`, {
        recipients: [], base_url: window.location.origin,
      });
      toast.success(t('auto.misc.inviteSentAgain'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/candidate-intake-invites/${id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="mt-3 border-t border-slate-200 pt-3 space-y-3" data-testid={`invite-panel-${link.id}`}>
      {canShare && (
        <div className="space-y-2">
          <Input
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder={t('auto.misc.emailAddressesCommaSeparated')}
            data-testid={`invite-emails-${link.id}`}
          />
          <Textarea
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('auto.misc.optionalNoteToIncludeInThe')}
            data-testid={`invite-message-${link.id}`}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={send} disabled={sending} data-testid={`invite-send-${link.id}`}>
              <Send className="h-3.5 w-3.5 mr-1.5" /> {sending ? t('auto.misc.sending') : t('auto.misc.emailThisForm')}
            </Button>
            <div className="flex rounded-md border border-slate-300 overflow-hidden text-xs">
              {[['en', 'EN'], ['pl', 'PL']].map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setInviteLang(code)}
                  className={`px-2.5 py-1.5 font-medium transition-colors ${
                    inviteLang === code ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`invite-lang-${code}-${link.id}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <span className="text-[11px] text-slate-500">
              {inviteLang === 'pl'
                ? 'Email and form open in Polish'
                : 'Email and form open in English'}
            </span>
          </div>
        </div>
      )}
      {invites.length === 0 ? (
        <p className="text-xs text-slate-500" data-testid={`invites-empty-${link.id}`}>
          {t('auto.misc.noInvitesSentFromThisLink')}
        </p>
      ) : (
        <ul className="space-y-1.5" data-testid={`invites-list-${link.id}`}>
          {invites.map((inv) => {
            const meta = INVITE_META[inv.status] || INVITE_META.sent;
            return (
              <li
                key={inv.id}
                className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5"
                data-testid={`invite-row-${inv.id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{inv.email}</p>
                  <p className="text-slate-500">
                    {inv.submitted_at
                      ? `Applied ${fmtDate(inv.submitted_at)}`
                      : inv.opened_at
                        ? `Opened ${fmtDate(inv.opened_at)}`
                        : inv.sent_at
                          ? `Sent ${fmtDate(inv.sent_at)}`
                          : inv.error || 'Not sent'}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {inv.language === 'pl' && (
                    <Badge className="bg-slate-200 text-slate-700 text-[10px]" data-testid={`invite-lang-badge-${inv.id}`}>PL</Badge>
                  )}
                  <Badge className={`${meta.color} text-[10px]`} data-testid={`invite-status-${inv.id}`}>
                    <meta.Icon className="h-3 w-3 mr-1" />{meta.label}
                  </Badge>
                  {canShare && inv.status !== 'submitted' && (
                    <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => resend(inv.id)} data-testid={`invite-resend-${inv.id}`}>
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                  )}
                  {canShare && (
                    <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => remove(inv.id)} data-testid={`invite-delete-${inv.id}`}>
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/** Reusable public intake links — send one to anyone, every submission lands here. */
const TemplatePicker = ({ templates, value, onChange, testId }) => {
  const { t } = useTranslation();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 sm:w-56 text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="default">{t('intakeTemplates.useDefault')}</SelectItem>
        {templates.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const IntakeLinks = ({ canShare }) => {
  const { t } = useTranslation();
  const [links, setLinks] = useState([]);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [openInvites, setOpenInvites] = useState(null);
  const [tplRefresh, setTplRefresh] = useState(0);
  const templates = useIntakeTemplates(tplRefresh);
  const [newTemplateId, setNewTemplateId] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/candidate-intake-links`);
      setLinks(data || []);
    } catch { setLinks([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const publicUrl = (tok) => `${window.location.origin}/candidate-intake/${tok}`;

  const create = async () => {
    if (!label.trim()) { toast.error(t('auto.misc.giveTheLinkALabelFirst')); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/candidate-intake-links`, { label: label.trim(), template_id: newTemplateId || null });
      setLabel('');
      toast.success(t('auto.misc.linkCreated'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  const copy = async (tok) => {
    try {
      await navigator.clipboard.writeText(publicUrl(tok));
      toast.success(t('auto.misc.linkCopiedSendItToThe'));
    } catch { toast.error(t('auto.misc.couldNotCopy')); }
  };

  const rotate = async (id) => {
    if (!window.confirm(t('auto.misc.rotateTheLinkTheOldUrl'))) return;
    try {
      await axios.post(`${API}/candidate-intake-links/${id}/rotate-token`);
      toast.success(t('auto.misc.newLinkGenerated'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggle = async (l) => {
    try {
      await axios.put(`${API}/candidate-intake-links/${l.id}`, {
        label: l.label, note: l.note, is_active: !l.is_active, template_id: l.template_id || null,
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const setLinkTemplate = async (l, tid) => {
    try {
      await axios.put(`${API}/candidate-intake-links/${l.id}`, {
        label: l.label, note: l.note, is_active: l.is_active, template_id: tid === 'default' ? null : tid,
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm(t('auto.misc.deleteThisLink'))) return;
    try {
      await axios.delete(`${API}/candidate-intake-links/${id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="intake-links-panel">
      <p className="text-sm text-slate-600">
        Send a link to anyone — they fill in their own details and a candidate card appears here.
        Rates, department and positions stay internal and are never asked for.
      </p>
      {canShare && (
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label, e.g. 2026 electricians"
            data-testid="intake-link-label-input"
          />
          <TemplatePicker templates={templates} value={newTemplateId || 'default'}
            onChange={(v) => setNewTemplateId(v === 'default' ? '' : v)} testId="intake-link-template-select" />
          <Button onClick={create} disabled={creating} className="shrink-0" data-testid="intake-link-create-btn">
            <Link2 className="h-4 w-4 mr-1.5" /> {t('auto.misc.createLink')}
          </Button>
        </div>
      )}
      <div className="border-t border-slate-200 pt-4">
        <IntakeTemplatesManager canShare={canShare} onChanged={() => setTplRefresh((n) => n + 1)} />
      </div>
      {links.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="intake-links-empty">{t('auto.misc.noLinksYet')}</p>
      ) : (
        <ul className="space-y-2">
          {links.map((l) => (
            <li
              key={l.id}
              className="rounded-lg border border-slate-200 p-3 space-y-2"
              data-testid={`intake-link-${l.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{l.label}</p>
                  <p className="text-xs text-slate-500">
                    {l.submissions || 0} submission{(l.submissions || 0) === 1 ? '' : 's'} · created {fmtDate(l.created_at)}
                  </p>
                </div>
                <Badge className={l.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                  {l.is_active ? t('auto.misc.active') : t('auto.misc.paused')}
                </Badge>
              </div>
              {canShare && (
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  {t('intakeTemplates.formLabel')}
                  <TemplatePicker templates={templates} value={l.template_id || 'default'}
                    onChange={(v) => setLinkTemplate(l, v)} testId={`intake-link-template-${l.id}`} />
                </div>
              )}
              <code className="block text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1.5 break-all">
                {publicUrl(l.share_token)}
              </code>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => copy(l.share_token)} data-testid={`intake-link-copy-${l.id}`}>
                  <Copy className="h-3.5 w-3.5 mr-1" /> {t('auto.misc.copy')}
                </Button>
                {canShare && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => toggle(l)} data-testid={`intake-link-toggle-${l.id}`}>
                      {l.is_active ? t('auto.misc.pause') : t('auto.misc.resume')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => rotate(l.id)} data-testid={`intake-link-rotate-${l.id}`}>
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> {t('auto.misc.rotate')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => remove(l.id)} data-testid={`intake-link-delete-${l.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenInvites((cur) => (cur === l.id ? null : l.id))}
                  data-testid={`intake-link-invites-${l.id}`}
                >
                  <Send className="h-3.5 w-3.5 mr-1" /> {openInvites === l.id ? t('auto.misc.hideInvites') : t('auto.misc.inviteTrack')}
                </Button>
              </div>
              {openInvites === l.id && <InvitePanel link={l} canShare={canShare} onSent={load} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Candidate database — the recruitment pipeline that sits beside
 * Employees. Hiring moves the card (same internal id) so nothing that
 * references the person breaks, and moving back restores it.
 */
export default function CandidatesPanel({ onMoved }) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [weekFilter, setWeekFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({ ...EMPTY_EMPLOYEE_FORM });
  const [editing, setEditing] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [linksOpen, setLinksOpen] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [mergeFor, setMergeFor] = useState(null);

  const canEdit = can('candidates.edit');
  const canCreate = can('candidates.create');
  const canDelete = can('candidates.delete');
  const canConvert = can('candidates.convert');
  const canShare = can('candidates.share');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/candidates`);
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load candidates');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live duplicate check while a candidate is being typed in or edited.
  const dialogOpen = addOpen || !!editing;
  const dupEmail = form.email;
  const dupPassport = form.passport_number;
  useEffect(() => {
    if (!dialogOpen || (!dupEmail && !dupPassport)) { setDupes([]); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/candidates/check-duplicates`, {
          params: { email: dupEmail || '', passport: dupPassport || '', exclude_id: editing?.id || '' },
        });
        setDupes(data.matches || []);
      } catch { setDupes([]); }
    }, 500);
    return () => clearTimeout(timer);
  }, [dialogOpen, dupEmail, dupPassport, editing?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && (r.candidate_status || 'new') !== statusFilter) return false;
      if (!matchesWeek(weekFilter, r.submitted_at || r.created_at)) return false;
      if (!q) return true;
      return ['name', 'email', 'phone', 'country', 'candidate_notes', 'intake_link_label']
        .some((k) => String(r[k] || '').toLowerCase().includes(q));
    });
  }, [rows, search, statusFilter, weekFilter]);

  const openEdit = (row) => {
    setEditing(row);
    setForm(populateFormFromEmployee(row));
    setNotes(row.candidate_notes || '');
  };

  const create = async (e) => {
    e.preventDefault();
    const payload = prepareSubmitData(form);
    if (!payload.name) { toast.error(t('auto.misc.nameIsRequired')); return; }
    try {
      await axios.post(`${API}/candidates`, {
        name: payload.name,
        candidate_status: 'new',
        candidate_notes: notes || null,
        data: payload,
      });
      toast.success(t('auto.misc.candidateAdded'));
      setAddOpen(false);
      setForm({ ...EMPTY_EMPLOYEE_FORM });
      setNotes('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/candidates/${editing.id}`, {
        ...prepareSubmitData(form),
        candidate_notes: notes || null,
      });
      toast.success(t('auto.misc.candidateUpdated'));
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const setStatus = async (row, status) => {
    try {
      await axios.put(`${API}/candidates/${row.id}`, { candidate_status: status });
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, candidate_status: status } : r)));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const promote = async (row) => {
    if (!window.confirm(`Move ${row.name} to Employees? Their card leaves Candidates.`)) return;
    try {
      await axios.post(`${API}/candidates/${row.id}/promote`);
      toast.success(`${row.name} is now an employee`);
      load();
      onMoved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete candidate ${row.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/candidates/${row.id}`);
      toast.success(t('auto.misc.candidateDeleted'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="candidates-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{t('auto.misc.candidates')}</h3>
          <p className="text-sm text-slate-600">
            {t('auto.misc.yourRecruitmentPipelineHireSomeoneAnd')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setLinksOpen(true)} data-testid="candidates-links-btn">
            <Share2 className="h-4 w-4 mr-1.5" /> {t('auto.misc.intakeLinks')}
          </Button>
          {canCreate && (
            <Button
              onClick={() => { setForm({ ...EMPTY_EMPLOYEE_FORM }); setNotes(''); setAddOpen(true); }}
              data-testid="add-candidate-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> {t('auto.misc.addCandidate')}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] sm:max-w-xs">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('auto.misc.searchNameEmailPhone')}
            className="pr-8"
            data-testid="candidate-search-input"
          />
          {search && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              onClick={() => setSearch('')}
              data-testid="candidate-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="candidates-week-filter" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[170px]" data-testid="candidate-status-filter">
            <SelectValue placeholder={t('auto.misc.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('auto.misc.allStatuses')}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 ml-auto" data-testid="candidates-result-count">
          {filtered.length} of {rows.length}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">{t('auto.misc.loading')}</p>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="candidates-empty">
          {t('auto.misc.noCandidatesYetAddOneOr')}
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((row) => {
            const meta = STATUS_META[row.candidate_status || 'new'] || STATUS_META.new;
            return (
              <Card
                key={row.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={(e) => {
                  if (e.target.closest?.('button, [role="button"], [role="dialog"]')) return;
                  if (canEdit) openEdit(row);
                }}
                data-testid={`candidate-card-${row.id}`}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{row.name}</p>
                      <p className="text-xs text-slate-500">
                        {row.source === 'intake_link'
                          ? `Applied${row.intake_link_label ? ` via ${row.intake_link_label}` : ''} · ${fmtDate(row.submitted_at || row.created_at)}`
                          : `Added ${fmtDate(row.created_at)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {row.duplicate_matches?.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium px-1.5 py-0.5 border border-amber-200"
                          title={`Looks like an existing record: ${row.duplicate_matches.map((m) => `${m.name} (${m.kind})`).join(', ')}`}
                          data-testid={`candidate-duplicate-badge-${row.id}`}
                        >
                          <AlertTriangle className="h-3 w-3" /> {t('auto.misc.duplicate')}
                        </span>
                      )}
                      <Badge className={`${meta.color} text-[11px]`}>{meta.label}</Badge>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-slate-600">
                    {row.email && <p className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3" /> {row.email}</p>}
                    {row.phone && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {row.phone}</p>}
                    {row.country && <p className="flex items-center gap-1.5"><Globe className="h-3 w-3" /> {row.country}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    {canEdit && (
                      <Select value={row.candidate_status || 'new'} onValueChange={(v) => setStatus(row, v)}>
                        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`candidate-status-${row.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {canConvert && (
                      <Button
                        size="sm"
                        className="h-8"
                        onClick={() => promote(row)}
                        data-testid={`candidate-promote-${row.id}`}
                      >
                        <UserCheck className="h-3.5 w-3.5 mr-1" /> {t('auto.misc.hire')}
                      </Button>
                    )}
                    {canEdit && row.duplicate_matches?.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-amber-300 text-amber-800"
                        onClick={() => setMergeFor(row)}
                        data-testid={`candidate-merge-${row.id}`}
                      >
                        <GitMerge className="h-3.5 w-3.5 mr-1" /> {t('auto.misc.merge')}
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={() => remove(row)}
                        data-testid={`candidate-delete-${row.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <MergeDialog
        candidate={mergeFor}
        onClose={() => setMergeFor(null)}
        onMerged={() => { setEditing(null); load(); onMoved?.(); }}
      />

      {/* Add candidate */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[1400px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('auto.misc.addCandidate')}</DialogTitle>
            <DialogDescription>{t('auto.misc.sameDetailsAsAnEmployeeRecord')}</DialogDescription>
          </DialogHeader>
          <DuplicateWarning matches={dupes} testId="candidate-add-duplicate-warning" />
          <div className="space-y-2">
            <Label className="text-xs">{t('auto.misc.recruiterNotes')}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={t('auto.misc.whereTheyCameFromAvailabilityExpectations')}
              data-testid="candidate-notes-input"
            />
          </div>
          <EmployeeForm employeeForm={form} setEmployeeForm={setForm} onSubmit={create} isEditing={false} showNotes={false} />
        </DialogContent>
      </Dialog>

      {/* Edit candidate */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-[1400px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Candidate — {editing?.name}</DialogTitle>
            <DialogDescription>
              {editing?.source === 'intake_link'
                ? 'Submitted by the candidate themselves. Add department, positions and rates here.'
                : 'Update the candidate record.'}
            </DialogDescription>
          </DialogHeader>
          <DuplicateWarning
            matches={dupes.length ? dupes : (editing?.duplicate_matches || [])}
            testId="candidate-edit-duplicate-warning"
          />
          {canEdit && (dupes.length > 0 || editing?.duplicate_matches?.length > 0) && (
            <Button
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-800"
              onClick={() => setMergeFor({
                ...editing,
                duplicate_matches: dupes.length ? dupes : editing.duplicate_matches,
              })}
              data-testid="candidate-edit-merge-btn"
            >
              <GitMerge className="h-4 w-4 mr-1.5" /> {t('auto.misc.mergeIntoTheExistingRecord')}
            </Button>
          )}
          {editing && (
            <Tabs defaultValue="details" className="space-y-4">
              <TabsList className="flex flex-wrap h-auto gap-1 p-1 w-full">
                <TabsTrigger value="details" data-testid="candidate-tab-details">{t('auto.misc.details')}</TabsTrigger>
                <TabsTrigger value="notes" data-testid="candidate-tab-notes">{t('auto.misc.notes')}</TabsTrigger>
                <TabsTrigger value="files" data-testid="candidate-tab-files">{t('auto.misc.attachments')}</TabsTrigger>
                {((editing?.intake_answers || []).length > 0 || (editing?.intake_opened_links || []).length > 0) && (
                  <TabsTrigger value="answers" data-testid="candidate-tab-answers">{t('intakeTemplates.answersTab')}</TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="answers" className="space-y-2" data-testid="candidate-intake-answers">
                {editing?.intake_template_name && <p className="text-xs text-slate-500">{t('intakeTemplates.answeredOn', { name: editing.intake_template_name })}</p>}
                {(editing?.intake_answers || []).map((a) => (
                  <div key={a.id} className="rounded border border-slate-200 p-2">
                    <p className="text-xs font-medium text-slate-600">{stripLinks(a.label)}</p>
                    <p className="text-sm text-slate-900 whitespace-pre-line">
                      {Array.isArray(a.value) ? (a.value.map(stripLinks).join(', ') || '—') : (a.type === 'yesno' ? (a.value ? t(`intakeTemplates.yesno.${a.value}`) : '—') : (stripLinks(a.value) || '—'))}
                    </p>
                  </div>
                ))}
                {(editing?.intake_opened_links || []).length > 0 && (
                  <div className="rounded border border-slate-200 p-2" data-testid="candidate-opened-links">
                    <p className="text-xs font-medium text-slate-600">{t('intakeTemplates.opened.title')}</p>
                    <ul className="mt-1 space-y-0.5 text-sm">
                      {editing.intake_opened_links.map((l) => (
                        <li key={l.href} className="flex flex-wrap items-center gap-2">
                          <span className="text-slate-900">{l.label}</span>
                          {l.required && <Badge variant="outline" className="text-[10px]">{t('intakeTemplates.opened.required')}</Badge>}
                          <span className="text-[11px] text-slate-500">{t('intakeTemplates.opened.at', { date: l.opened_at ? new Date(l.opened_at).toLocaleString() : '—' })}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="details">
                <EmployeeForm employeeForm={form} setEmployeeForm={setForm} onSubmit={saveEdit} isEditing showNotes={false} />
              </TabsContent>
              <TabsContent value="notes" className="space-y-3">
                <Label className="text-xs">{t('auto.misc.recruiterNotes')}</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={6}
                  data-testid="candidate-edit-notes"
                />
                <div className="flex justify-end">
                  <Button onClick={saveEdit} data-testid="candidate-save-notes">{t('auto.misc.save')}</Button>
                </div>
              </TabsContent>
              <TabsContent value="files">
                <CandidateFiles candidateId={editing.id} canEdit={canEdit} />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Intake links */}
      <Dialog open={linksOpen} onOpenChange={setLinksOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('auto.misc.candidateIntakeLinks')}</DialogTitle>
            <DialogDescription>{t('auto.misc.reusableSelfServiceFormsJustLike')}</DialogDescription>
          </DialogHeader>
          <IntakeLinks canShare={canShare} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
